import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "../styles/Dashboard.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from "recharts";

const statusLabels = {
  1: "Lucrari cu Probleme",
  2: "Lucrari Active",
  3: "Montaje Finalizate",
  4: "Dosare Prosumatori",
  5: "Certificate de Racordare",
  6: "Vizite de teren",
  7: "Depus la MIPE",
  8: "Decedati & Rosu",
};

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [statusLogs, setStatusLogs] = useState([]);
  const ITEMS_PER_PAGE = 10; // Definește numărul de elemente pe pagină
  /*const [visitLogs, setVisitLogs] = useState([]);
  const [currentPageVisit, setCurrentPageVisit] = useState(1);
  const visitsPerPage = 5; // Număr de vizite pe pagină*/
  const [currentPageTranzitie, setCurrentPageTranzitie] = useState(1);
  const [showAvertizarePopup, setShowAvertizarePopup] = useState(false);
  const COLORS = ["#00C49F", "#FFBB28", "#FF8042", "#FF4C4C", "#7D3C98", "#5DADE2", "#E91E63"];

  const filteredMessages = messages;


  const flagIsTrue = (v) => String(v).trim() === "1";

  const hasVoucherDoc = (c) =>
    flagIsTrue(c.factura_voucher) ||
    (Array.isArray(c.documents) && c.documents.some(d => d.document_type === "factura_voucher"));
    
  const hasFacturaDelgaz = (c) => 
    flagIsTrue(c.factura_delgaz) ||
    (Array.isArray(c.documents) && c.documents.some(d => d.document_type === "factura_delgaz"));

  const hasDovadaDelgaz = (c) =>
    flagIsTrue(c.dovada_delgaz) ||
    (Array.isArray(c.documents) && c.documents.some(d => d.document_type === "dovada_delgaz"));
  

  // Acceptă fie un flag pe client, fie o listă de documente cu type='certificat_racordare'
  const hasCertificatRacordare = (c) =>
    String(c?.has_certificat_racordare ?? "") === "1" ||
    String(c?.certificat_racordare ?? "") === "1";
  
  const hasMaintenanceProblem = (client) =>
    flagIsTrue(client?.cu_probleme);
  
  const countClientsByStatus = (status) => {
    return clients.filter((client) => {
      const clientStatus = String(client.status).trim();
  
      // „Probleme” este o categorie suplimentară.
      if (String(status) === "1") {
        return (
          clientStatus === "1" ||
          hasMaintenanceProblem(client)
        );
      }
  
      if (String(status) === "5") {
        return (
          clientStatus === "5" ||
          hasCertificatRacordare(client)
        );
      }
  
      return clientStatus === String(status);
    }).length;
  };
  
  const tranzitieStats = {
    total: clients.length,
    probleme: countClientsByStatus("1"),
    in_asteptare: countClientsByStatus("2"),
    montajfinalizat: countClientsByStatus("3"),
    dosarprosumator: countClientsByStatus("4"),
    certificat: countClientsByStatus("5"),
    vizite_teren: countClientsByStatus("6"),
    depus_mipe: countClientsByStatus("7"),
    decedati: countClientsByStatus("8"),
  };

  const getClientForLog = (log) => {
    return clients.find(c => String(c.id) === String(log.client_id));
  };

  const allowedStatusesForRole3 = new Set(["1", "2", "3", "6", "8"]);
  const isRole3 = String(user?.role) === "3";

  const logsTranzitie = statusLogs
  .filter((log) => Boolean(getClientForLog(log)))
  .filter(
    (log) =>
      !isRole3 ||
      allowedStatusesForRole3.has(
        String(log.old_status)
      ) ||
      allowedStatusesForRole3.has(
        String(log.new_status)
      )
  );

  const totalPagesTranzitie = Math.ceil(
    logsTranzitie.length / ITEMS_PER_PAGE
  );

  const indexOfLastItemTranzitie =
    currentPageTranzitie * ITEMS_PER_PAGE;

  const indexOfFirstItemTranzitie =
    indexOfLastItemTranzitie - ITEMS_PER_PAGE;

  const currentLogsTranzitie = logsTranzitie.slice(
    indexOfFirstItemTranzitie,
    indexOfLastItemTranzitie
  );

  const facturaVoucherCount = clients.filter(
    (client) => hasVoucherDoc(client)
  ).length;

  const facturiDelgazNeplatiteCount = clients.filter(
    (client) =>
      hasFacturaDelgaz(client) &&
      !hasDovadaDelgaz(client)
  ).length;

  const facturiDelgazPlatiteCount = clients.filter(
    (client) => hasDovadaDelgaz(client)
  ).length;

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  
    const fetchClients = axios.get("https://tj.marenergy.ro/api.php?action=get_clients");
    const fetchMessages = axios.get("https://tj.marenergy.ro/api.php?action=get_dashboard_messages");
    const fetchStatusLogs = axios.get("https://tj.marenergy.ro/api.php?action=get_status_logs");
    //const fetchVisitLogs = axios.get("https://tj.marenergy.ro/api.php?action=get_visit_logs");
  
    Promise.all([fetchClients, fetchMessages, fetchStatusLogs, /*fetchVisitLogs*/])
      .then(([clientsRes, messagesRes, statusLogsRes, /*visitLogsRes*/]) => {
        if (clientsRes.data) setClients(clientsRes.data);
        if (messagesRes.data) setMessages(messagesRes.data);
        if (statusLogsRes.data) setStatusLogs(statusLogsRes.data);
        /*if (visitLogsRes.data) setVisitLogs(visitLogsRes.data);*/
      })
      .catch((error) => {
        console.error("Eroare la încărcarea datelor:", error);
      });
  
  }, [user, navigate]);

  const handleNavigateToClients = (status) => {
    if (String(status) === "1") {
      navigate("/clients?probleme=1");
    } else if (String(status) === "5") {
      navigate("/clients?cfr=1");
    } else {
      navigate(`/clients?status=${status}`);
    }
  };
  
  const handleNavigateToFacturaVoucher = () => {
    navigate("/clients?factura_voucher=1");
  };
  
  const handleNavigateToFacturaDelgaz = () => {
    navigate(
      "/clients?factura_delgaz=1&fara_dovada_delgaz=1"
    );
  };
  
  const handleNavigateToDovadaDelgaz = () => {
    navigate("/clients?dovada_delgaz=1");
  };

  const getEligibleClients = () => {
    const sevenDaysAgo = new Date();
  
    sevenDaysAgo.setDate(
      sevenDaysAgo.getDate() - 7
    );
  
    return clients.filter((client) => {
      if (String(client.status) !== "3") {
        return false;
      }
  
      const lastUpdate = new Date(
        client.last_status_update
      );
  
      if (isNaN(lastUpdate.getTime())) {
        return false;
      }
  
      return lastUpdate <= sevenDaysAgo;
    });
  };
  
  const clientiAvertizati = getEligibleClients();
  const [paginaAvertizare, setPaginaAvertizare] = useState(1);
  const clientiPerPagina = 5;
  
  const indexStart = (paginaAvertizare - 1) * clientiPerPagina;
  const clientiAfisati = clientiAvertizati.slice(indexStart, indexStart + clientiPerPagina);
  const totalPagini = Math.ceil(clientiAvertizati.length / clientiPerPagina);
  
  const getClientiFaraCertificatRacordare = () => {
    const twentyFiveDaysAgo = new Date();
    twentyFiveDaysAgo.setDate(twentyFiveDaysAgo.getDate() - 25);
  
    return clients.filter((c) => {
      if (hasCertificatRacordare(c)) return false;
  
      const firstStatus4Log = statusLogs
        .filter(
          (log) =>
            String(log.client_id) === String(c.id) &&
            String(log.new_status) === "4"
        )
        .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))[0];
  
      if (!firstStatus4Log) return false;
  
      const firstStatus4Date = new Date(firstStatus4Log.updated_at);
      if (isNaN(firstStatus4Date.getTime())) return false;
  
      return firstStatus4Date <= twentyFiveDaysAgo;
    });
  };
  
  const clientiFaraCertificatRacordare = getClientiFaraCertificatRacordare();
  const [showCertificatPopup, setShowCertificatPopup] = useState(false);
  const [paginaCertificat, setPaginaCertificat] = useState(1);
  const totalPaginiCertificat = Math.ceil(
    clientiFaraCertificatRacordare.length / clientiPerPagina
  );
  
  const [sortCertificat, setSortCertificat] = useState({
    key: "zile",
    direction: "desc",
  });
  
  const getFirstStatus4Log = (client) =>
    statusLogs
      .filter(
        (log) =>
          String(log.client_id) === String(client.id) &&
          String(log.new_status) === "4"
      )
      .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at))[0];
  
  const handleSortCertificat = (key) => {
    setPaginaCertificat(1);
    setSortCertificat((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };
  
  const getSortIcon = (key) => {
    if (sortCertificat.key !== key) return "↕️";
    return sortCertificat.direction === "asc" ? "↑" : "↓";
  };
  
  const clientiCertificatSortati = [...clientiFaraCertificatRacordare].sort(
    (a, b) => {
      const firstLogA = getFirstStatus4Log(a);
      const firstLogB = getFirstStatus4Log(b);
  
      const firstDateA = firstLogA ? new Date(firstLogA.updated_at) : null;
      const firstDateB = firstLogB ? new Date(firstLogB.updated_at) : null;
  
      const zileA = firstDateA
        ? Math.floor((new Date() - firstDateA) / (1000 * 60 * 60 * 24))
        : 0;
  
      const zileB = firstDateB
        ? Math.floor((new Date() - firstDateB) / (1000 * 60 * 60 * 24))
        : 0;
  
      const values = {
        id: [Number(a.id), Number(b.id)],
        client: [a.name || "", b.name || ""],
        adresa: [a.address || "", b.address || ""],
        status: [
          statusLabels[a.status] || a.status || "",
          statusLabels[b.status] || b.status || "",
        ],
        primaIntrare: [
          firstDateA ? firstDateA.getTime() : 0,
          firstDateB ? firstDateB.getTime() : 0,
        ],
        zile: [zileA, zileB],
      };
  
      const [valueA, valueB] = values[sortCertificat.key];
  
      let result =
        typeof valueA === "number" && typeof valueB === "number"
          ? valueA - valueB
          : String(valueA).localeCompare(String(valueB), "ro", {
              sensitivity: "base",
            });
  
      return sortCertificat.direction === "asc" ? result : -result;
    }
  );
  
  const clientiCertificatAfisati = clientiCertificatSortati.slice(
    (paginaCertificat - 1) * clientiPerPagina,
    paginaCertificat * clientiPerPagina
  );

  const handleAddMessage = () => {
    if (!["1", "2"].includes(String(user?.role))) {
      alert("Nu aveți permisiunea de a adăuga mesaje!");
      return;
    }
  
    const message = newMessage.trim();
  
    if (!message) {
      alert("Introduceți un mesaj valid!");
      return;
    }
  
    axios
      .post(
        "https://tj.marenergy.ro/api.php",
        {
          action: "add_dashboard_message",
          message: message,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      )
      .then((response) => {
        setMessages((prevMessages) => [
          {
            id: response.data?.id ?? Date.now(),
            message: message,
            created_at: new Date().toISOString(),
          },
          ...prevMessages,
        ]);
  
        setNewMessage("");
      })
      .catch((error) => {
        alert(
          error.response?.data?.message ||
          "Eroare la adăugarea mesajului"
        );
      });
  };

  const handleDeleteMessage = (messageId) => {
    if (!["1", "2"].includes(String(user?.role))) {
      alert("Nu aveți permisiunea de a șterge mesaje!");
      return;
    }
  
    axios
      .post(
        "https://tj.marenergy.ro/api.php",
        {
          action: "delete_dashboard_message",
          id: messageId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      )
      .then(() => {
        setMessages((prevMessages) =>
          prevMessages.filter(
            (message) => String(message.id) !== String(messageId)
          )
        );
      })
      .catch((error) => {
        alert(
          error.response?.data?.message ||
          "Eroare la ștergerea mesajului"
        );
      });
  };

  const filteredStatusesTranzitie =
  Object.entries(statusLabels).filter(([status]) => {
    const role = String(user?.role);
    const restrictedStatuses = ["4", "5", "7"];

    if (
      restrictedStatuses.includes(status) &&
      !["1", "2"].includes(role)
    ) {
      return false;
    }

    if (!["1", "2", "3"].includes(role)) {
      return false;
    }

    return countClientsByStatus(status) > 0;
  });
  
  const [onlineCount, setOnlineCount] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnlinePopover, setShowOnlinePopover] = useState(false);
  const canSeeOnlineUsers =
    String(user?.role) === "1" || String(user?.role) === "2";

  const fetchOnlineUsers = async () => {
    if (!canSeeOnlineUsers) return;
  
    const token = localStorage.getItem("token");
    if (!token) return;
  
    try {
      const res = await axios.get(
        "https://tj.marenergy.ro/api.php?action=active_users_list",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOnlineUsers(res.data?.users ?? []);
    } catch (e) {
      setOnlineUsers([]);
    }
  };      

  // 🔎 Quick Search
  const [qs, setQs] = useState("");
  const [qsOpen, setQsOpen] = useState(false);
  const [qsIndex, setQsIndex] = useState(0);
  const [qsSelected, setQsSelected] = useState(null);

  // normalizare pt. căutare (ignore diacritice)
  const normalize = (s = "") =>
    s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  
  const quickSearchPool = useMemo(
    () => clients || [],
    [clients]
  );

  // rezultate filtrate
  const quickResults = useMemo(() => {
    if (!qs.trim()) return [];
    const q = normalize(qs);
    return quickSearchPool.filter(c =>
      normalize(c.name).includes(q) ||
      normalize(c.address || "").includes(q) ||
      String(c.id).includes(q)
    );
  }, [qs, quickSearchPool]);

  const onSelectClient = (client) => {
    setQsSelected(client);
    setQs(`${client.name} (#${client.id})`);
    setQsOpen(false);
    setQsIndex(0);
  };

  const onKeyDownQuick = (e) => {
    if (!qsOpen || quickResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setQsIndex(i => Math.min(i + 1, quickResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setQsIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = quickResults[qsIndex];
      if (c) onSelectClient(c);
    } else if (e.key === "Escape") {
      setQsOpen(false);
    }
  };

  const rowTranzitieRef = useRef(null);

  const [isSlideLayout, setIsSlideLayout] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1200 : true
  );  

  useEffect(() => {
    const onResize = () => setIsSlideLayout(window.innerWidth < 1200);
    onResize(); // setează corect de la primul render
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);  


  // --- flags / refs pentru control ---
  const userInteractedRef = useRef(false);
  const alignedOnceRef = useRef({
    tranzitie: false,
  });

  // marchează că userul a atins sau a derulat
  useEffect(() => {
    const onInteract = () => { userInteractedRef.current = true; };
    const rows = [rowTranzitieRef.current];
    rows.forEach(r => {
      if (!r) return;
      r.addEventListener("pointerdown", onInteract, { passive: true });
      r.addEventListener("wheel", onInteract, { passive: true });
      r.addEventListener("touchstart", onInteract, { passive: true });
    });
    return () => {
      rows.forEach(r => {
        if (!r) return;
        r.removeEventListener("pointerdown", onInteract);
        r.removeEventListener("wheel", onInteract);
        r.removeEventListener("touchstart", onInteract);
      });
    };
  }, []);

  // aliniază O SINGURĂ DATĂ la stânga + fallback fără snap dacă primul card e tăiat
  const alignLeftOnceAndFix = useCallback((rowEl, key) => {
    if (!rowEl || !key) return;

    // dacă am făcut deja align sau userul a interacționat, nu mai atinge nimic
    if (alignedOnceRef.current[key] || userInteractedRef.current) return;

    // oprire temporară snap ca să nu sară
    const hadInline = rowEl.style.scrollSnapType !== "";
    const prevSnap = getComputedStyle(rowEl).scrollSnapType; // ex: "x mandatory"
    rowEl.style.scrollSnapType = "none";

    // aliniază la capătul stâng
    rowEl.scrollLeft = 0;

    // verifică dacă primul card e tăiat; dacă da -> dezactivează snap pe containerul ăsta
    // 2) verifică dacă primul card e tăiat — dacă da, rămâne fără snap (fallback)
    const first = rowEl.querySelector("[data-status]");   
    if (first) {
      const c = rowEl.getBoundingClientRect();            
      const f = first.getBoundingClientRect();
      const padLeft = parseFloat(getComputedStyle(rowEl).paddingLeft) || 0;
      const firstIsCut = (f.left - c.left) > (padLeft + 1);
      if (firstIsCut) {
        rowEl.classList.add("no-snap");     
      }
    }
    // restaurează snap DOAR dacă nu suntem în fallback .no-snap
    if (!rowEl.classList.contains("no-snap")) {
      requestAnimationFrame(() => {
        if (hadInline) rowEl.style.scrollSnapType = prevSnap || "x mandatory";
        else rowEl.style.scrollSnapType = ""; // lasă CSS-ul
      });
    }

    // marcat „am aliniat o dată”
    alignedOnceRef.current[key] = true;
  }, []);

  // AUTO-ALIGN o singură dată când intri pe tab / pe mobil
  useEffect(() => {
    if (!isSlideLayout) return;

    const el = rowTranzitieRef.current;

    if (!el) return;

    // 1) oprește snap-ul DOAR la init și aliniază la capătul stâng
    el.classList.add('init-nosnap');
    el.scrollLeft = 0;

    // 2) verifică dacă primul card e tăiat — dacă da, rămâne fără snap (fallback)
    const first = el.querySelector('[data-status]');
    if (first) {
      const c = el.getBoundingClientRect();
      const f = first.getBoundingClientRect();
      const padLeft = parseFloat(getComputedStyle(el).paddingLeft) || 0;
      const firstIsCut = (f.left - c.left) > (padLeft + 1);
      if (firstIsCut) {
        el.classList.add('no-snap'); // fallback definitiv pt. device-uri problematice
      }
    }

    // 3) după 1–2 frame-uri, repornește snap-ul (doar dacă n-am intrat în fallback)
    let raf1 = requestAnimationFrame(() => {
      let raf2 = requestAnimationFrame(() => {
        if (!el.classList.contains('no-snap')) {
          el.classList.remove('init-nosnap');
          requestAnimationFrame(() => { el.scrollLeft = 0; }); // întărire
        }
      });
      el._raf2 = raf2;
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (el._raf2) cancelAnimationFrame(el._raf2);
    };
  }, [isSlideLayout]);

  // NU mai re-alinia pe fiecare resize de pe Android (bara de adresă tot schimbă înălțimea)
  // Dacă vrei neapărat, fă doar la schimbări MAJORE de lățime/orientație:
  useEffect(() => {
    if (!isSlideLayout) return;

    let lastW = window.innerWidth;
    const onResize = () => {
      const w = window.innerWidth;
      if (Math.abs(w - lastW) < 40) return;   // ignoră micile fluctuații
      lastW = w;
      // nu re-alinia dacă userul a atins deja
      if (userInteractedRef.current) return;

      alignLeftOnceAndFix(
        rowTranzitieRef.current,
        "tranzitie"
      );
    };

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [isSlideLayout, alignLeftOnceAndFix]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
  
    let alive = true;
  
    const pingAndFetch = async () => {
      try {
        // 1) heartbeat
        await axios.post(
          "https://tj.marenergy.ro/api.php",
          { action: "heartbeat" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
  
        // 2) get count
        const res = await axios.get(
          "https://tj.marenergy.ro/api.php?action=active_users",
          { headers: { Authorization: `Bearer ${token}` } }
        );
  
        if (alive) setOnlineCount(res.data?.count ?? 0);
      } catch (e) {
        if (alive) setOnlineCount(null);
      }
    };
  
    pingAndFetch();
    const interval = setInterval(pingAndFetch, 15000); // 15s
  
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, []);  


  return (
    <div className="container">

      <div className="user-info d-flex justify-content-between align-items-start">
        <div className="user-badges">
          <Link
            to={`/user/${user?.user_id || user?.id}`}
            className="up-user-name"
          >
            👤 {user?.name || "Utilizator"}
          </Link>

          <div
            className={`online-wrap ${!canSeeOnlineUsers ? "disabled" : ""}`}
            onMouseEnter={() => {
              if (!canSeeOnlineUsers) return;
              setShowOnlinePopover(true);
              fetchOnlineUsers();
            }}
            onMouseLeave={() => setShowOnlinePopover(false)}
            onPointerDown={() => {
              if (!canSeeOnlineUsers) return;
              setShowOnlinePopover(true);
              fetchOnlineUsers();
            }}
            onPointerUp={() => setShowOnlinePopover(false)}
            onPointerCancel={() => setShowOnlinePopover(false)}
          >
            <span className="user-online">
              🟢 Online acum: {onlineCount === null ? "—" : onlineCount}
            </span>

            {showOnlinePopover && canSeeOnlineUsers && (
              <div className="online-popover">
                <div className="online-popover-title">
                  Utilizatori online ({onlineUsers.length})
                </div>

                {onlineUsers.length === 0 ? (
                  <div className="online-popover-empty">Nimeni online acum</div>
                ) : (
                  <ul className="online-popover-list">
                    {onlineUsers.map((u) => (
                      <li key={u.id} className="online-popover-item">
                        <span className="dot" /> {u.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

        </div>
        <button className="btn btn-danger" onClick={logout}>
          Logout
        </button>
      </div>
      <div className="d-flex justify-content-between align-items-center my-4">
        <img src="/images/Logo.webp" alt="Logo" className="dashboard-logo" />
      </div>

      {/* Tab-uri */}
      <div className="tranzitie-justa-title">
        TRANZIȚIE JUSTĂ
      </div>

      <div
        className={`row justify-content-center ${
          filteredStatusesTranzitie.length === 1
            ? "single-card"
            : ""
        }`}
        ref={rowTranzitieRef}
      >
        {filteredStatusesTranzitie.map(
          ([status, label]) => (
            <div
              className="col-md-4"
              key={status}
              data-status={status}
            >
              <div
                className={`stat-card ${
                  Number(status) === 1
                    ? "stat-card-red"
                    : ""
                }`}
              >
                <div className="stat-icon">
                  {status === "1" && (
                    <i className="fas fa-exclamation-triangle" />
                  )}
                  {status === "2" && (
                    <i className="fas fa-tools" />
                  )}
                  {status === "3" && (
                    <i className="fas fa-check-circle" />
                  )}
                  {status === "4" && (
                    <i className="fas fa-file-contract" />
                  )}
                  {status === "5" && (
                    <i className="fas fa-folder-plus" />
                  )}
                  {status === "6" && (
                    <i className="fas fa-map-marker-alt" />
                  )}
                  {status === "7" && (
                    <i className="fas fa-check-circle" />
                  )}
                  {status === "8" && (
                    <i className="fas fa-skull" />
                  )}
                </div>

                <div className="stat-content">
                  <h3>{label}</h3>

                  <p className="stat-number">
                    {countClientsByStatus(status)}
                  </p>

                  <button
                    className="btn btn-outline-primary mt-2"
                    onClick={() =>
                      handleNavigateToClients(status)
                    }
                  >
                    Vezi proiectele
                  </button>
                </div>
              </div>
            </div>
          )
        )}

        {["1", "2"].includes(String(user?.role)) &&
          facturaVoucherCount > 0 && (
            <div className="col-md-4 d-flex">
              <div className="stat-card flex-fill">
                <div className="stat-icon">
                  <i className="fas fa-receipt" />
                </div>

                <div className="stat-content">
                  <h3>Facturate</h3>

                  <p className="stat-number">
                    {facturaVoucherCount}
                  </p>

                  <button
                    className="btn btn-outline-primary mt-2"
                    onClick={handleNavigateToFacturaVoucher}
                  >
                    Vezi proiectele
                  </button>
                </div>
              </div>
            </div>
          )}

        {["1", "2"].includes(String(user?.role)) &&
          facturiDelgazNeplatiteCount > 0 && (
            <div className="col-md-4 d-flex">
              <div className="stat-card flex-fill">
                <div className="stat-icon">
                  <i className="fas fa-receipt" />
                </div>

                <div className="stat-content">
                  <h3>Facturi DelGaz neplătite</h3>

                  <p className="stat-number">
                    {facturiDelgazNeplatiteCount}
                  </p>

                  <button
                    className="btn btn-outline-primary mt-2"
                    onClick={handleNavigateToFacturaDelgaz}
                  >
                    Vezi proiectele
                  </button>
                </div>
              </div>
            </div>
          )}

        {["1", "2"].includes(String(user?.role)) &&
          facturiDelgazPlatiteCount > 0 && (
            <div className="col-md-4 d-flex">
              <div className="stat-card flex-fill">
                <div className="stat-icon">
                  <i className="fas fa-receipt" />
                </div>

                <div className="stat-content">
                  <h3>Facturi DelGaz plătite</h3>

                  <p className="stat-number">
                    {facturiDelgazPlatiteCount}
                  </p>

                  <button
                    className="btn btn-outline-primary mt-2"
                    onClick={handleNavigateToDovadaDelgaz}
                  >
                    Vezi proiectele
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>

      <hr className="my-2" />

      {/* 🔎 Cautare rapida client */}
      <div className="quick-search card p-3 mt-4">
        <label className="form-label fw-bold mb-2">Cautare rapida client</label>

        <div className="quick-search-input-group">
          <input
            className="form-control quick-search-input"
            type="text"
            placeholder="Cauta dupa nume, ID sau adresa..."
            value={qs}
            onChange={(e) => {
              setQs(e.target.value);
              setQsOpen(true);
              setQsSelected(null);
            }}
            onFocus={() => qs && setQsOpen(true)}
            onKeyDown={onKeyDownQuick}
          />

          {qs && (
            <button
              className="btn btn-light quick-search-clear"
              onClick={() => {
                setQs("");
                setQsSelected(null);
                setQsOpen(false);
              }}
              title="Curata"
            >
              ✕
            </button>
          )}

          <button
            className="btn btn-primary"
            disabled={!qsSelected}
            onClick={() => {
              if (qsSelected) navigate(`/clients/${qsSelected.id}`);
            }}
          >
            Viziteaza profilul
          </button>
        </div>

        {qsOpen && (
          <>
            {quickResults.length > 0 ? (
              <ul className="quick-search-list">
                {quickResults.map((c, idx) => (
                  <li
                    key={c.id}
                    className={`quick-search-item ${idx === qsIndex ? "active" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // previne blur pe input
                      onSelectClient(c);
                    }}
                  >
                    <div className="qs-line">
                      <span className="qs-name">{c.name}</span>
                      <span className="qs-id">#{c.id}</span>
                    </div>
                    <div className="qs-meta">
                      <span className="qs-address">{c.address || "Adresa indisponibila"}</span>
                      <span className="qs-status">{statusLabels[c.status] || "Status necunoscut"}</span>
                      {String(user?.role) === "1" && String(c?.facturat_vulnerabili) === "1" && (
                        <span className="text-success fw-bold ms-2">FACTURAT</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : qs.trim() ? (
              <div className="quick-search-empty">Niciun client gasit.</div>
            ) : null}
          </>
        )}
      </div>

      {(String(user?.role) === "1" || String(user?.role) === "2") && clientiFaraCertificatRacordare.length > 0 && (
        <div
          className="custom-restrictie-alert"
          style={{
            backgroundColor: "#fff3cd",
            border: "2px dashed #ffc107",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "15px",
          }}
        >
          <div
            className="custom-restrictie-alert-header"
            tabIndex={0}
            onClick={() => {
              setPaginaCertificat(1);
              setShowCertificatPopup(true);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <span className="custom-restrictie-alert-icon">⚠️</span>
            <span className="custom-restrictie-alert-title">
              {clientiFaraCertificatRacordare.length} client
              {clientiFaraCertificatRacordare.length > 1 ? "i" : ""} fara certificat de racordare dupa 25 zile de la intrarea in Dosare Prosumatori
            </span>
          </div>
        </div>
      )}

      {(String(user?.role) === "1" || String(user?.role) === "2") && clientiAvertizati.length > 0 && (
        <div
          className="custom-restrictie-alert"
          style={{
            backgroundColor: "#fff3cd",          // galben pal (background)
            border: "2px dashed #ffc107",        // border dashed galben
            borderRadius: "8px",                 // colțuri rotunjite (opțional dar recomandat)
            padding: "12px",                     // spațiu interior
            marginBottom: "15px",                // spațiu jos (dacă mai ai componente sub)
          }}
        >
          <div
            className="custom-restrictie-alert-header"
            tabIndex={0}
            onClick={() => {
              setPaginaAvertizare(1);       // 👈 resetează la prima pagină
              setShowAvertizarePopup(true); // 👈 deschide popup-ul
            }}            
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
          >
            <span className="custom-restrictie-alert-icon">⚠️</span>
            <span className="custom-restrictie-alert-title">
              {clientiAvertizati.length} client{clientiAvertizati.length > 1 ? "i" : ""} cu montaj finalizat de peste 7 zile
            </span>
          </div>
        </div>
      )}

      {user?.role === "2" && (
        <div className="custom-restrictie-alert">
          <div className="custom-restrictie-alert-header" tabIndex={0}>
            <span className="custom-restrictie-alert-icon">❗</span>
            <span className="custom-restrictie-alert-title">De ce nu pot sterge seriile si documentele clientilor mei?</span>
          </div>
          <div className="custom-restrictie-alert-message">
            Seriile si documentele clientului sunt blocate din motive de siguranta si trasabilitate 
            a lucrarilor. Daca este orice tip de greseala, contactati echipa tehnica. 24/7.
          </div>
        </div>
      )}

      <div className="custom-restrictie-alert2">
        <div className="custom-restrictie-alert2-header" tabIndex={0}>
          <span className="custom-restrictie-alert2-icon">❗</span>
          <span className="custom-restrictie-alert2-title">Inainte de a lua decizia de a inlocui un tablou cu busoane cu unul cu sigurante, va rugam luati legatura cu ing. Paizan pentru parerea finala. Multumim!</span>
        </div>
        <div className="custom-restrictie-alert2-message">
          <p>Telefon ing. Paizan: 0733.795.707</p>
          <p>Telefon ing. Nadabaica: 0768.003.797</p>
          <p>Materialele necesare schimbării tablourilor se afla la depozitul central. Contactati-l pe Romi Chirica pentru aceste materiale și trebuie cerute separat/client.</p>
          <p>Telefon Romi: 0766.429.759</p>
        </div>
      </div>

      <div className="custom-restrictie-alert2">
        <div className="custom-restrictie-alert2-header" tabIndex={0}>
          <span className="custom-restrictie-alert2-icon">❗</span>
          <span className="custom-restrictie-alert2-title">Va rugam sa tineti cont de faptul ca pozele incarcate pe platforma ajung direct la decont.</span>
        </div>
        <div className="custom-restrictie-alert2-message">
          Pozele incarcate pe pagina beneficiarilor trebuie sa aibe o claritate cat mai buna. 
          Dupa montaj, echipa de consultanti preia pozele si le introduce in documentele pentru decont. 
          Va rugam sa nu adaugati text pe poze.
        </div>
      </div>

      <div className="mt-5">
        <h2 className="text-center">📢 Mesaje Generale</h2>
        <div className="card p-4 shadow mt-3">
          <ul className="list-group">
            {filteredMessages.length > 0 ? (
              filteredMessages.map((msg) => (
                <li key={msg.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <span>{msg.message}</span>
                  {(user.role === "1" || user.role === "2") && (
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteMessage(msg.id)}>
                      Sterge
                    </button>
                  )}
                </li>
              ))
            ) : (
              <p className="text-muted">Niciun mesaj disponibil</p>
            )}
          </ul>
          {["1", "2"].includes(String(user?.role)) && (
            <div className="generale-message-form">
              <input
                type="text"
                className="form-control"
                placeholder="Adauga un mesaj..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <button className="btn btn-primary ml-2" onClick={handleAddMessage}>
                Adauga Mesaj
              </button>
            </div>
          )}
        </div>
      </div>

      {/*<div className="mt-5">
        <h2>📜 Istoric Vizite de Teren</h2>
        <div className="card p-4 shadow mt-3">
          <ul className="list-group">
          {visitLogs.length > 0 ? (
            visitLogs
              .slice((currentPageVisit - 1) * visitsPerPage, currentPageVisit * visitsPerPage)
              .map((log, index) => {
                const nameRegex = new RegExp(log.client_name, "i");
                const parts = log.message.split(nameRegex);

                return (
                  <li key={index} className="list-group-item">
                    <strong>{new Date(log.created_at).toLocaleString()} </strong>
                    {parts[0]}
                    <Link 
                      to={`/clients/${log.client_id}`} 
                      style={{ textDecoration: "none", fontWeight: "bold", color: "inherit", cursor: "pointer" }}
                    >
                      {log.client_name}
                    </Link>
                    {parts[1]}
                  </li>
                );
              })
          ) : (
            <p className="text-muted">Nicio vizita de teren inregistrata.</p>
          )}
          </ul>

          {Math.ceil(visitLogs.length / visitsPerPage) > 1 && (
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination">
                <li className={`page-item ${currentPageVisit === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPageVisit(currentPageVisit - 1)}>Înapoi</button>
                </li>
                <li className={`page-item ${currentPageVisit === Math.ceil(visitLogs.length / visitsPerPage) ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPageVisit(currentPageVisit + 1)}>Înainte</button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>*/}

      <hr className="my-2" />

      {["1", "2"].includes(String(user?.role)) && (
        <div className="chart-container">
          <h3 className="chart-title">
            📈 Grafic progres - Tranzitie Justa
          </h3>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  name: "Montaj Finalizat",
                  total: tranzitieStats.montajfinalizat,
                },
                {
                  name: "Probleme",
                  total: tranzitieStats.probleme,
                },
                {
                  name: "Vizite de Teren",
                  total: tranzitieStats.vizite_teren,
                },
                {
                  name: "Așteaptă Montaj",
                  total: tranzitieStats.in_asteptare,
                },
                {
                  name: "Dosar Prosumator",
                  total: tranzitieStats.dosarprosumator,
                },
                {
                  name: "Cu C.F.R.",
                  total: tranzitieStats.certificat,
                },
                {
                  name: "Depus MIPE",
                  total: tranzitieStats.depus_mipe,
                },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />

              <Bar dataKey="total">
                {COLORS.map((color, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={color}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {(String(user?.role) === "1" || String(user?.role) === "2") && (
        <hr className="my-2" />
      )}

      <div className="mt-5">
        <h2 className="text-center">📜 Istoric Actualizari - Tranzitie Justa</h2>
        <div className="card p-4 shadow mt-3">
          <ul className="list-group">
            {currentLogsTranzitie.length > 0 ? (
              currentLogsTranzitie.map((log, index) => {
                const client = getClientForLog(log);
                return (
                  <li key={index} className="list-group-item">
                    <strong>{new Date(log.updated_at).toLocaleString()} </strong>
                    {log.user_id ? (
                      <Link
                        to={`/user/${log.user_id}`}
                        style={{ textDecoration: "none", color: "inherit", fontWeight: "bold", cursor: "pointer" }}
                      >
                        - {log.user_name || "Necunoscut"}
                      </Link>
                    ) : (
                      <span style={{ fontWeight: "bold" }}>{log.user_name || "Necunoscut"}</span>
                    )}{" "}
                    a actualizat statusul clientului{" "}
                    <Link
                      to={`/clients/${log.client_id}`}
                      style={{ textDecoration: "none", color: "inherit", fontWeight: "bold", cursor: "pointer" }}
                    >
                      {client?.name || "Necunoscut"}
                    </Link>{" "}
                    din{" "}
                    <Link
                      to={`/clients?status=${log.old_status}`}
                      style={{ textDecoration: "none", fontWeight: "bold", color: "inherit", cursor: "pointer" }}
                    >
                      {statusLabels[log.old_status] || "Necunoscut"}
                    </Link>{" "}
                    in{" "}
                    <Link
                      to={`/clients?status=${log.new_status}`}
                      style={{ textDecoration: "none", fontWeight: "bold", color: "inherit", cursor: "pointer" }}
                    >
                      {statusLabels[log.new_status] || "Necunoscut"}
                    </Link>.
                  </li>
                );
              })
            ) : (
              <p className="text-muted">Nicio modificare de status inregistrata.</p>
            )}
          </ul>

          {totalPagesTranzitie > 1 && (
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination">
                <li className={`page-item ${currentPageTranzitie === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPageTranzitie(currentPageTranzitie - 1)}>Inapoi</button>
                </li>
                <li className={`page-item ${currentPageTranzitie === totalPagesTranzitie ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPageTranzitie(currentPageTranzitie + 1)}>Inainte</button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>

      {showCertificatPopup && (
        <div className="popup-overlay">
          <div
            className="popup-content"
            style={{
              width: "95%",
              maxWidth: "1300px",
              overflowX: "auto",
            }}
          >
            <button
              className="popup-close"
              onClick={() => setShowCertificatPopup(false)}
            >
              ×
            </button>

            <h4
              className="mb-3"
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                lineHeight: "1.4",
              }}
            >
              Clienti fara certificat de racordare dupa 25 zile de la prima intrare
              in Dosare Prosumatori
            </h4>

            <div style={{ overflowX: "auto" }}>
              <table
                className="table table-striped"
                style={{
                  minWidth: "1050px",
                  verticalAlign: "middle",
                }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSortCertificat("id")} style={{ cursor: "pointer" }}>
                      ID {getSortIcon("id")}
                    </th>

                    <th onClick={() => handleSortCertificat("client")} style={{ cursor: "pointer" }}>
                      Client {getSortIcon("client")}
                    </th>

                    <th onClick={() => handleSortCertificat("adresa")} style={{ cursor: "pointer" }}>
                      Adresa beneficiar {getSortIcon("adresa")}
                    </th>

                    <th onClick={() => handleSortCertificat("status")} style={{ cursor: "pointer" }}>
                      Status actual {getSortIcon("status")}
                    </th>

                    <th onClick={() => handleSortCertificat("primaIntrare")} style={{ cursor: "pointer" }}>
                      Prima intrare status 4 {getSortIcon("primaIntrare")}
                    </th>

                    <th onClick={() => handleSortCertificat("zile")} style={{ cursor: "pointer" }}>
                      Zile trecute {getSortIcon("zile")}
                    </th>

                    <th>Actiune</th>
                  </tr>
                </thead>

                <tbody>
                  {clientiCertificatAfisati.map((client) => {
                    const firstStatus4Log = getFirstStatus4Log(client);

                    const firstDate = firstStatus4Log
                      ? new Date(firstStatus4Log.updated_at)
                      : null;

                    const zileTrecute = firstDate
                      ? Math.floor(
                          (new Date() - firstDate) / (1000 * 60 * 60 * 24)
                        )
                      : "-";

                    return (
                      <tr key={client.id}>
                        <td>
                          <strong>#{client.id}</strong>
                        </td>

                        <td
                          style={{
                            minWidth: "180px",
                            fontWeight: "600",
                          }}
                        >
                          {client.name}
                        </td>

                        <td
                          style={{
                            minWidth: "260px",
                            maxWidth: "320px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: "1.4",
                          }}
                        >
                          {client.address || "-"}
                        </td>

                        <td>
                          <span
                            className="badge bg-warning text-dark"
                            style={{
                              fontSize: "13px",
                              padding: "8px",
                            }}
                          >
                            {statusLabels[client.status] || client.status}
                          </span>
                        </td>

                        <td>
                          {firstDate
                            ? firstDate.toLocaleDateString("ro-RO")
                            : "-"}
                        </td>

                        <td>
                          <strong style={{ color: "#dc3545" }}>
                            {zileTrecute}
                          </strong>
                        </td>

                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/clients/${client.id}`)}
                          >
                            Vezi client
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPaginiCertificat > 1 && (
              <div
                className="d-flex justify-content-between align-items-center mt-3"
                style={{
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="btn btn-outline-secondary"
                  disabled={paginaCertificat === 1}
                  onClick={() => setPaginaCertificat(paginaCertificat - 1)}
                >
                  Inapoi
                </button>

                <span style={{ fontWeight: "600" }}>
                  Pagina {paginaCertificat} din {totalPaginiCertificat}
                </span>

                <button
                  className="btn btn-outline-secondary"
                  disabled={paginaCertificat === totalPaginiCertificat}
                  onClick={() => setPaginaCertificat(paginaCertificat + 1)}
                >
                  Inainte
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showAvertizarePopup && (
        <div className="popup-overlay" onClick={() => setShowAvertizarePopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h4 className="mb-3">
              Clienti care au montaj finalizat de cel putin 7 zile dar nu s-a depus pentru dosarul de prosumator
            </h4>

            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Nume</th>
                  <th>Adresa</th>
                  <th>Status</th>
                  <th>Ultima actualizare</th>
                </tr>
              </thead>
              <tbody>
                {clientiAfisati.map((client) => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{client.address}</td>
                    <td>{statusLabels[client.status]}</td>
                    <td>{new Date(client.last_status_update).toLocaleDateString("ro-RO")}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPagini > 1 && (
              <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setPaginaAvertizare((prev) => Math.max(prev - 1, 1))}
                  disabled={paginaAvertizare === 1}
                >
                  ⬅️
                </button>
                <span>
                  Pagina {paginaAvertizare} din {totalPagini}
                </span>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setPaginaAvertizare((prev) => Math.min(prev + 1, totalPagini))}
                  disabled={paginaAvertizare === totalPagini}
                >
                  ➡️
                </button>
              </div>
            )}

            <button
              className="btn btn-secondary mt-3"
              onClick={() => setShowAvertizarePopup(false)}
            >
              Inchide
            </button>
          </div>
        </div>
      )}

      <hr />    
    </div>
  );
};

export default Dashboard;